import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWallet } from '../../context/WalletContext';
import { 
  FaSignOutAlt,
  FaChevronLeft,
  FaChevronRight,
  FaShieldAlt,
  FaCoins,
  FaUser,
  FaChartPie,
  FaWallet,
  FaBook,
  FaHistory,
  FaHammer,
  FaHeadset,
  FaCalculator,
  FaGift
} from 'react-icons/fa';
import { 
  HiOutlineViewGrid, 
  HiOutlineSupport,
  HiOutlineDocumentText
} from 'react-icons/hi';


const Sidebar = ({ collapsed, setCollapsed, activeTab, setActiveTab }) => {
  const { account, isAdmin, disconnectWallet, isConnected } = useWallet();
  const [showSupportSubmenu, setShowSupportSubmenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const shortenAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const mainNavItems = [
    { id: 'overview', icon: <HiOutlineViewGrid size={20} />, label: 'Overview', description: 'Dashboard overview' },
    { id: 'mine', icon: <FaHammer size={18} />, label: 'Mine', description: 'Mine DOLP tokens' },
    { id: 'claim', icon: <FaGift size={18} />, label: 'Claim', description: 'Claim vested tokens' },
    { id: 'wallet', icon: <FaWallet size={18} />, label: 'Wallet', description: 'Your wallet' },
    { id: 'referral', icon: <FaUser size={18} />, label: 'Referral', description: 'Referral program' },
    { id: 'levels', icon: <FaBook size={18} />, label: 'Packages', description: 'Educational PDFs' },
    { id: 'user-analytics', icon: <FaCalculator size={18} />, label: 'User Analytics', description: 'User analytics dashboard' },
    { id: 'history', icon: <FaHistory size={18} />, label: 'History', description: 'Transaction history' },
  ];

  const handleNavigation = (tabId) => {
    setActiveTab(tabId);
    if (location.pathname === '/admin') {
      navigate('/dashboard');
    }
  };

  if (!isConnected) return null;

  return (
    // ADD THIS WRAPPER DIV
    <div
      className={
        `sidebar-desktop-wrapper bg-gradient-to-br from-white to-gray-100 dark:from-gray-950 dark:to-gray-900 border-r border-gray-200 dark:border-gray-800 min-h-screen h-full flex flex-col transition-all duration-300 ease-in-out`
      }
      style={{
        width: collapsed ? '4rem' : '16rem',
        minWidth: collapsed ? '4rem' : '16rem',
        maxWidth: collapsed ? '4rem' : '16rem',
        overflowX: 'hidden',
        zIndex: 30
      }}
    >
      <div className={`sidebar-inner ${collapsed ? 'collapsed' : ''} flex flex-col h-full p-2 md:p-3 gap-1 transition-all duration-300 ease-in-out`}> 
        {/* Logo Section */}
        <div className="sidebar-header flex items-center justify-between mb-2">
          <button 
            className="collapse-toggle p-1 rounded-md bg-gray-100 dark:bg-gray-800 hover:bg-cyan-100 dark:hover:bg-cyan-900 border border-gray-200 dark:border-gray-700 transition-all"
            onClick={() => setCollapsed(!collapsed)} 
            aria-label="Toggle sidebar"
          >
            {collapsed ? <FaChevronRight size={18} /> : <FaChevronLeft size={18} />}
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="sidebar-nav flex-1">
          <div className="nav-section mb-1">
            {!collapsed && <span className="nav-section-title text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">MAIN</span>}
            {mainNavItems.map(item => (
              <button
                key={item.id}
                className={`sidebar-link flex items-center w-full px-2 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === item.id && location.pathname !== '/admin' ? 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-200 border-l-4 border-cyan-400' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
                style={{ marginBottom: '0.25rem' }}
                onClick={() => handleNavigation(item.id)}
                data-tooltip={collapsed ? item.label : null}
              >
                <span className="sidebar-link-icon mr-2">{item.icon}</span>
                {!collapsed && (
                  <span className="sidebar-link-text flex-1 text-left">{item.label}</span>
                )}
                {!collapsed && activeTab === item.id && location.pathname !== '/admin' && (
                  <span className="active-indicator w-2 h-2 bg-cyan-400 rounded-full ml-2"></span>
                )}
              </button>
            ))}
          </div>

          {/* Admin section - only visible to contract owner */}
          <div className="nav-section mb-1">
            {!collapsed && <span className="nav-section-title text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">ADMIN</span>}
            {isAdmin ? (
              <>
                <button
                  className={`sidebar-link ${activeTab && activeTab.startsWith('admin-') ? 'active' : ''}`}
                  onClick={() => handleNavigation('admin-overview')}
                  data-tooltip={collapsed ? 'Admin Panel' : null}
                >
                  <span className="sidebar-link-icon mr-2"><FaShieldAlt size={18} /></span>
                  {!collapsed && (
                    <span className="sidebar-link-text flex-1 text-left">Admin Panel</span>
                  )}
                </button>
                {!collapsed && (
                  <div className="admin-subtabs flex flex-col gap-1 mt-1">
                    <button 
                      className={`sidebar-sublink px-2 py-1 rounded-md text-xs font-medium flex items-center gap-2 ${activeTab === 'admin-overview' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
                      onClick={() => handleNavigation('admin-overview')}
                    >
                      <HiOutlineViewGrid size={14} className="mr-1" /> Overview
                    </button>
                    <button 
                      className={`sidebar-sublink px-2 py-1 rounded-md text-xs font-medium flex items-center gap-2 ${activeTab === 'admin-mining-tokenomics' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
                      onClick={() => handleNavigation('admin-mining-tokenomics')}
                    >
                      <FaHammer size={12} className="mr-1" /> Mining & Tokenomics
                    </button>
                    <button 
                      className={`sidebar-sublink px-2 py-1 rounded-md text-xs font-medium flex items-center gap-2 ${activeTab === 'admin-treasury-security' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
                      onClick={() => handleNavigation('admin-treasury-security')}
                    >
                      <FaCoins size={12} className="mr-1" /> Treasury & Security
                    </button>
                    <button 
                      className={`sidebar-sublink px-2 py-1 rounded-md text-xs font-medium flex items-center gap-2 ${activeTab === 'admin-analytics' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
                      onClick={() => handleNavigation('admin-analytics')}
                    >
                      <FaChartPie size={12} className="mr-1" /> Admin Analytics
                    </button>
                    <button 
                      className={`sidebar-sublink px-2 py-1 rounded-md text-xs font-medium flex items-center gap-2 ${activeTab === 'admin-user-activity' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
                      onClick={() => handleNavigation('admin-user-activity')}
                    >
                      <FaUser size={12} className="mr-1" /> User Activity
                    </button>
                  </div>
                )}
              </>
            ) : null}
          </div>

          <div className="nav-section nav-section-bottom mt-auto mb-0 pb-0">
            {!collapsed && <span className="nav-section-title text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">SUPPORT</span>}
            <button 
              className={`sidebar-link flex items-center w-full px-2 py-2 rounded-lg text-sm font-medium transition-all ${showSupportSubmenu ? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-200' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
              onClick={() => !collapsed && setShowSupportSubmenu(!showSupportSubmenu)}
              data-tooltip={collapsed ? 'Help & Support' : null}
            >
              <span className="sidebar-link-icon mr-2"><HiOutlineSupport size={18} /></span>
              {!collapsed && (
                <>
                  <span className="sidebar-link-text flex-1 text-left">Help & Support</span>
                  <span className={`submenu-arrow ml-2 ${showSupportSubmenu ? 'open' : ''}`}>
                    <FaChevronRight size={10} />
                  </span>
                </>
              )}
            </button>
            {!collapsed && showSupportSubmenu && (
              <div className="submenu flex flex-col gap-1 mt-1 ml-4">
                <button 
                  type="button" 
                  className="submenu-link flex items-center gap-2 px-2 py-1 rounded-md text-xs font-medium hover:bg-cyan-50 dark:hover:bg-cyan-900/30"
                  onClick={() => window.open('mailto:support@example.com', '_blank')}
                >
                  <FaHeadset size={12} />
                  <span>Contact Us</span>
                </button>
                <button 
                  type="button" 
                  className="submenu-link flex items-center gap-2 px-2 py-1 rounded-md text-xs font-medium hover:bg-cyan-50 dark:hover:bg-cyan-900/30"
                  onClick={() => window.open('https://docs.example.com', '_blank')}
                >
                  <HiOutlineDocumentText size={12} />
                  <span>Documentation</span>
                </button>
              </div>
            )}
          </div>
        </nav>
        
        {/* User Section */}
        <div className="sidebar-footer mt-4">
          {!collapsed ? (
            <div className="user-card flex items-center gap-3 p-2 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-100 dark:border-cyan-800 mb-2">
              <div className="user-avatar w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-cyan-400/30 to-purple-400/20">
                <span className="text-lg">🐬</span>
              </div>
              <div className="user-info flex flex-col">
                <span className="user-address text-xs font-semibold text-gray-700 dark:text-gray-200">{shortenAddress(account)}</span>
                <span className="user-network text-[10px] text-cyan-600 dark:text-cyan-200 flex items-center gap-1">
                  <span className="network-dot w-2 h-2 bg-cyan-400 rounded-full inline-block"></span>
                  Polygon
                </span>
              </div>
            </div>
          ) : (
            <div className="collapsed-user flex flex-col items-center gap-2">
              <div className="user-avatar-small w-7 h-7 rounded-full flex items-center justify-center bg-gradient-to-br from-cyan-400/30 to-purple-400/20" data-tooltip="Connected">
                <span className="text-base">🐬</span>
                <span className="online-indicator-small w-2 h-2 bg-cyan-400 rounded-full absolute bottom-0 right-0"></span>
              </div>
              <button 
                className="logout-btn-small p-1 rounded-md bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800 border border-red-200 dark:border-red-700 transition-all"
                onClick={disconnectWallet}
                data-tooltip="Disconnect"
              >
                <FaSignOutAlt size={14} />
              </button>
            </div>
          )}
          {!collapsed && (
            <button 
              className="logout-btn w-full py-2 rounded-lg bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-200 font-semibold flex items-center justify-center gap-2 mt-2 transition-all"
              onClick={disconnectWallet}
              title="Disconnect"
            >
              <FaSignOutAlt size={16} />
              <span>Logout</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Sidebar;